import React from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, ArrowRight, ExternalLink, Smartphone,
  X, Maximize2, ChevronLeft, ChevronRight, Github
} from "lucide-react";

/* ── data ─────────────────────────────────────────────── */
const PROJECTS = {
  SiPattina: {
    name: "Sì Pattina",
    tagline: "Live Group Ride Tracking",
    category: "Sports · React + Firebase",
    live: "https://si-pattini.web.app/welcome",
    playStore: "https://play.google.com/store/apps/details?id=com.sipattini.app",
    description:
      "Sì Pattina is where a city's inline skating group organises its rides. An organiser creates a weekly or one-off event, shares it with a single link, and skaters confirm they are coming. When the ride starts, the organiser's phone records the GPS route while everyone who joined watches the line draw itself across the map in real time.\n\nWhen the ride ends it becomes something to keep and share: a route card, a transparent sticker, a short video of the path animating over the map, or a GPX file that drops straight into Insta360 Studio, GoPro Quik and Garmin VIRB to overlay speed and distance onto action footage. A live speed leaderboard crowns the three fastest skaters of the night, and the crown stays on the winner's profile.\n\nPrivacy was a design constraint, not an afterthought. Solo rides never leave the device — the track is drawn, measured and turned into an image entirely on the phone, and deleted from the phone. Only rides you actually joined are shared with the people who were there.\n\nBuilt for the Italian skating community and shipped in Italian, wrapped with Capacitor for Android alongside the web app.",
    tech: ["React", "Firebase", "Live GPS Tracking", "Geolocation API", "Interactive Maps", "GPX Export", "Canvas Rendering", "Video Generation", "Capacitor", "Firestore Security Rules"],
    count: 5,
  },
  Management: {
    name: "ManB2B",
    tagline: "Field Team Management Platform",
    category: "Business · React + Firebase",
    live: "https://manp2p.web.app",
    description:
      "ManB2B gives a company running field teams one place for every job, every hour and every person. A manager creates jobs and schedules them — on chosen weekdays or until a deadline — while any single day can still be moved or cancelled on its own without touching the rest of the series.\n\nEach role opens on the screen that matters to them. Managers see the whole company and assign supervisors and crews in bulk. Supervisors get a day-by-day calendar of their own jobs and can add or swap workers on a session. Workers see only today's jobs with times and address, and one button to start and stop.\n\nHours record themselves. Workers tap start and stop, missing check-outs are flagged, and a supervisor can correct a time without rewriting past records. Every session carries photos and a short report, plus anything extra used or missing. At the end of the month an hourly rate or a price per job turns the whole thing into a ready PDF invoice.\n\nIt runs in the browser on phones, tablets and computers with nothing to install, in English, Arabic, Italian and Spanish — with a full right-to-left layout in Arabic. Built for cleaning companies, maintenance teams, contractors and field service providers.",
    tech: ["React", "Firebase Authentication", "Firestore", "Role-Based Access Control", "Recurring Schedules", "Time Tracking", "PDF Invoice Generation", "Photo Upload & Storage", "i18n (4 Languages)", "Full RTL Layout"],
    count: 3,
  },
  PocketLinks: {
    name: "PocketLinks",
    tagline: "Smart Link Manager App",
    category: "Productivity · Flutter",
    playStore: "https://play.google.com/store/apps/details?id=com.auryxstudio.pocketlinks",
    description:
      "PocketLinks helps users save, organize, and manage important links from across different apps in one place. It integrates seamlessly with the Android share system, allowing users to save links directly from browsers, social media, and other applications with a single tap.\n\nUsers can assign custom titles to links, group them into categories, and easily edit, delete, copy, or open any link at any time. All data is stored locally, ensuring fast performance, offline access, and complete user privacy.\n\nDesigned for developers, students, researchers, and content creators who need quick and organized access to their saved resources.",
    tech: ["Flutter", "Dart", "SQLite", "Android Share Intent", "Offline Storage", "Custom Form Validation", "Clean UI Architecture"],
    count: 3,
  },
  nmoney: {
    name: "Nmoney",
    tagline: "Smart Money Manager",
    category: "Finance · Flutter",
    playStore: "https://play.google.com/store/apps/details?id=com.auryxstudio.nmoney",
    description:
      "Nmoney is a powerful finance app built with Flutter designed to help users manage personal finances in a smart, organized way. Track daily income and expenses, manage multiple jobs or income sources, and calculate earnings based on hourly rates.\n\nSupports different job types — normal jobs with hourly rates and free or fixed jobs — giving users full flexibility. The intuitive interface makes financial tracking easy even for non-technical users.\n\nIdeal for freelancers, employees, and anyone who wants better control over their money and wants to build better financial habits.",
    tech: ["Flutter", "Dart", "Firebase Realtime DB", "Firebase Firestore", "Local Data Persistence", "State Management", "Clean Architecture", "Date & Time Calculations"],
    count: 3,
  },
  church: {
    name: "Church Community App",
    tagline: "Community & Events Platform",
    category: "Community · Flutter",
    description:
      "A modern community app built for church members featuring events, media, announcements, and member profiles. The app helps keep the community connected with real-time updates and a clean, welcoming interface.\n\nMembers can browse upcoming events, access media content, receive important announcements, and manage their profiles — all in one place.",
    tech: ["Flutter", "Firebase", "Firebase Authentication", "Cloud Storage"],
    count: 4,
  },
  tozbest: {
    name: "TozBest",
    tagline: "Collaborative Task Manager",
    category: "Productivity · Flutter",
    description:
      "TozBest is a collaborative task manager that helps you track goals, share progress, and keep friends motivated with real-time updates. Create tasks, assign them, set deadlines, and watch your team succeed together.\n\nBuilt with Firebase Realtime Database for instant synchronization across all devices, with push notifications to keep everyone on track.",
    tech: ["Flutter", "Firebase Realtime DB", "Push Notifications"],
    count: 3,
  },
  modern3d: {
    name: "Modern 3D Concept",
    tagline: "Futuristic UI Design Concept",
    category: "Design · Flutter",
    description:
      "A futuristic 3D animated app concept showcasing smooth, immersive transitions between chat, e-shop, and profile screens. Built to demonstrate advanced Flutter animation capabilities and modern design principles.\n\nEvery screen uses depth, parallax, and layered elements to create a truly three-dimensional feel — pushing the boundaries of what's possible with Flutter UI.",
    tech: ["Flutter", "Rive Animations", "3D UI/UX Design", "Custom Transitions"],
    count: 8,
  },
  Caffezo: {
    name: "Caffezo",
    tagline: "Multi-Tenant Restaurant SaaS",
    category: "SaaS · React + Firebase",
    live: "https://caffezo.com",
    description:
      "Caffezo is a commercial SaaS platform for restaurants, bars and cafés across Europe, sold on three subscription tiers. Each business builds its own branded page — categories, products, colours and the whole customer experience — and appears on the Caffezo platform itself, so new customers can discover it.\n\nGuests scan a QR code, browse a premium digital menu, order from the table, and can play interactive games while they wait, with a leaderboard that gives a venue a reason to be returned to.\n\nOwners get the operational side: a dashboard tracking orders from day to month, automatic sales totals, staff shift monitoring that records who worked and for how long, automatic payroll calculation, and a table booking and reservation system on the top tier.\n\nThe platform ships in 8 languages — English, Italian, Arabic, Turkish, German, Spanish, Greek and French — on a multi-tenant Firebase architecture where every venue's data stays isolated behind security rules.",
    tech: ["React", "Firebase", "Multi-Tenant Architecture", "Subscription Tiers", "QR Menu System", "8 Languages", "Order Management", "Shift & Payroll Automation", "Table Booking", "Analytics Dashboard"],
    count: 8,
  },
};

/* ── helpers ──────────────────────────────────────────── */
function TechBadge({ label }) {
  return (
    <span className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-medium
      bg-white/5 border border-white/10 text-slate-300 hover:border-cyan-400/40
      hover:text-cyan-300 transition-colors">
      {label}
    </span>
  );
}

/* ── fullscreen lightbox ──────────────────────────────── */
function Lightbox({ src, onClose, onPrev, onNext, total, current }) {
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNext();
      if (e.key === "ArrowLeft") onPrev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-lg"
      onClick={onClose}
    >
      {/* Close */}
      <button onClick={onClose}
        className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center
          rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10">
        <X size={18} />
      </button>

      {/* Prev */}
      {total > 1 && (
        <button onClick={(e) => { e.stopPropagation(); onPrev(); }}
          className="absolute left-5 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center
            justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10">
          <ChevronLeft size={22} />
        </button>
      )}
      {total > 1 && (
        <button onClick={(e) => { e.stopPropagation(); onNext(); }}
          className="absolute right-5 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center
            justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10">
          <ChevronRight size={22} />
        </button>
      )}

      {/* Image */}
      <motion.img
        key={current}
        src={src}
        alt="Screenshot fullscreen"
        initial={{ scale: 0.88, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.88, opacity: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 22 }}
        className="max-h-[90vh] max-w-[88vw] rounded-2xl object-contain
          shadow-[0_0_80px_-10px_rgba(56,189,248,0.3)]"
        onClick={(e) => e.stopPropagation()}
      />

      {/* Counter */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono text-xs
        text-slate-400 bg-white/8 px-3 py-1 rounded-full border border-white/10">
        {current + 1} / {total}
      </div>
    </motion.div>
  );
}

/* ── main component ───────────────────────────────────── */
export default function ProjectDetails() {
  const { id } = useParams();
  const project = PROJECTS[id];

  const [current, setCurrent] = React.useState(0);
  const [lightbox, setLightbox] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);
  const touchStartX = React.useRef(0);

  const next = React.useCallback(() => {
    if (project) setCurrent((p) => (p + 1) % project.count);
  }, [project]);
  const prev = React.useCallback(() => {
    if (project) setCurrent((p) => (p - 1 + project.count) % project.count);
  }, [project]);

  /* auto-advance */
  React.useEffect(() => {
    if (!project || isHovered || lightbox) return;
    const t = setTimeout(next, 5000);
    return () => clearTimeout(t);
  }, [project, current, isHovered, lightbox, next]);

  /* scroll to top */
  React.useEffect(() => { window.scrollTo({ top: 0 }); }, [id]);

  /* keyframes */
  React.useEffect(() => {
    if (document.getElementById("pd-keyframes")) return;
    const s = document.createElement("style");
    s.id = "pd-keyframes";
    s.innerHTML = `
      @keyframes pgrow { from{width:0} to{width:100%} }
      @keyframes gshift { 0%,100%{background-position:0% 50%} 50%{background-position:100% 50%} }
    `;
    document.head.appendChild(s);
  }, []);

  if (!project) {
    return (
      <div className="min-h-screen bg-[#050816] flex flex-col items-center justify-center gap-4 text-white">
        <p className="text-slate-400">Project not found.</p>
        <Link to="/" className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors">
          <ArrowLeft size={15} /> Back to Portfolio
        </Link>
      </div>
    );
  }

  const imgs = Array.from({ length: project.count }, (_, i) =>
    require(`./assets/${id}/${i + 1}.png`)
  );

  return (
    <div className="min-h-screen bg-[#050816] text-white">

      {/* ── sticky top nav ── */}
      <nav className="sticky top-0 z-50 flex items-center justify-between px-5 md:px-10 py-4
        bg-[#050816]/85 backdrop-blur-xl border-b border-white/8">
        <Link to="/"
          className="flex items-center gap-2 text-sm font-medium text-slate-400
            hover:text-white transition-colors group">
          <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform" />
          Portfolio
        </Link>

        <span className="hidden sm:block text-sm font-semibold text-white/80 truncate max-w-xs">
          {project.name}
        </span>

        <div className="flex items-center gap-2">
          {project.playStore && (
            <a href={project.playStore} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold
                bg-emerald-500/15 border border-emerald-400/30 text-emerald-300
                hover:bg-emerald-500/25 transition-all">
              <Smartphone size={12} /> Play Store
            </a>
          )}
          {project.live && (
            <a href={project.live} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold
                bg-cyan-400/10 border border-cyan-400/30 text-cyan-300
                hover:bg-cyan-400/20 transition-all">
              <ExternalLink size={12} /> Live Site
            </a>
          )}
        </div>
      </nav>

      {/* ── hero banner ── */}
      <div className="relative overflow-hidden border-b border-white/8">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/8 via-transparent to-fuchsia-600/8" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_0%,rgba(56,189,248,0.07),transparent)]" />
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 py-12 md:py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
          >
            <span className="inline-block font-mono text-xs text-cyan-400/80 uppercase
              tracking-widest mb-3 px-3 py-1 rounded-full border border-cyan-400/20 bg-cyan-400/5">
              {project.category}
            </span>
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-3
              bg-gradient-to-r from-white via-slate-200 to-cyan-300 bg-clip-text text-transparent">
              {project.name}
            </h1>
            <p className="text-slate-400 text-lg">{project.tagline}</p>
          </motion.div>
        </div>
      </div>

      {/* ── main content ── */}
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="flex flex-col lg:flex-row gap-10">

          {/* ──────── LEFT: image showcase ──────── */}
          <div className="flex-1 min-w-0">

            {/* Main slider */}
            <div
              className="relative rounded-2xl overflow-hidden bg-[#0a0b18]
                border border-white/8 shadow-[0_0_60px_-20px_rgba(56,189,248,0.2)]"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onTouchStart={(e) => { touchStartX.current = e.changedTouches[0].screenX; }}
              onTouchEnd={(e) => {
                const dx = touchStartX.current - e.changedTouches[0].screenX;
                if (dx > 50) next();
                if (dx < -50) prev();
              }}
            >
              {/* progress bar */}
              <div
                key={`bar-${current}`}
                className="absolute top-0 left-0 h-[2px] z-20 rounded-full"
                style={{
                  animationName: "pgrow, gshift",
                  animationDuration: "5s, 3s",
                  animationTimingFunction: "linear, ease",
                  animationIterationCount: "1, infinite",
                  animationPlayState: isHovered || lightbox ? "paused" : "running",
                  background: "linear-gradient(90deg,#22d3ee,#818cf8,#f472b6,#22d3ee)",
                  backgroundSize: "400% 400%",
                }}
              />

              {/* image */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={current}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.35 }}
                  className="flex items-center justify-center px-6 py-8 min-h-[340px] md:min-h-[480px]"
                >
                  <img
                    src={imgs[current]}
                    alt={`${project.name} screenshot ${current + 1}`}
                    className="max-h-[420px] md:max-h-[520px] w-auto rounded-xl object-contain
                      cursor-zoom-in drop-shadow-2xl"
                    onClick={() => setLightbox(true)}
                  />
                </motion.div>
              </AnimatePresence>

              {/* expand btn */}
              <button
                onClick={() => setLightbox(true)}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center
                  rounded-lg bg-white/8 hover:bg-white/15 border border-white/10
                  text-slate-400 hover:text-white transition-all"
                title="View fullscreen"
              >
                <Maximize2 size={13} />
              </button>

              {/* arrows */}
              {project.count > 1 && (
                <>
                  <button
                    onClick={prev}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center
                      justify-center rounded-full bg-black/50 hover:bg-black/70
                      border border-white/10 text-white transition-all backdrop-blur-sm"
                  >
                    <ArrowLeft size={15} />
                  </button>
                  <button
                    onClick={next}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center
                      justify-center rounded-full bg-black/50 hover:bg-black/70
                      border border-white/10 text-white transition-all backdrop-blur-sm"
                  >
                    <ArrowRight size={15} />
                  </button>
                </>
              )}

              {/* counter */}
              <div className="absolute bottom-4 right-4 font-mono text-[11px] text-slate-500
                bg-black/50 px-2.5 py-1 rounded-full border border-white/8 backdrop-blur-sm">
                {current + 1} / {project.count}
              </div>
            </div>

            {/* Thumbnail strip */}
            {project.count > 1 && (
              <div className="flex gap-2.5 mt-4 overflow-x-auto pb-1 scrollbar-hide">
                {imgs.map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrent(i)}
                    className={`flex-shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-xl overflow-hidden
                      border-2 transition-all ${i === current
                        ? "border-cyan-400 opacity-100 scale-105"
                        : "border-white/10 opacity-50 hover:opacity-80 hover:border-white/30"
                      }`}
                  >
                    <img src={src} alt={`thumb ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ──────── RIGHT: sidebar info ──────── */}
          <div className="lg:w-80 xl:w-96 flex flex-col gap-5 lg:sticky lg:top-24 lg:self-start">

            {/* About card */}
            <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-6">
              <h3 className="text-xs font-mono font-semibold uppercase tracking-widest
                text-cyan-400/80 mb-4">About this project</h3>
              <div className="space-y-3">
                {project.description.split("\n\n").map((para, i) => (
                  <p key={i} className="text-sm text-slate-400 leading-relaxed">{para}</p>
                ))}
              </div>
            </div>

            {/* Tech stack */}
            <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-6">
              <h3 className="text-xs font-mono font-semibold uppercase tracking-widest
                text-cyan-400/80 mb-4">Tech Stack</h3>
              <div className="flex flex-wrap gap-2">
                {project.tech.map((t) => <TechBadge key={t} label={t} />)}
              </div>
            </div>

            {/* Links */}
            <div className="flex flex-col gap-2.5">
              {project.live && (
                <a
                  href={project.live}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2.5 py-3 px-5 rounded-xl
                    font-semibold text-sm
                    bg-gradient-to-r from-cyan-500 to-fuchsia-600
                    hover:from-cyan-400 hover:to-fuchsia-500
                    shadow-[0_0_24px_-6px_rgba(56,189,248,0.5)]
                    transition-all duration-300"
                >
                  <ExternalLink size={15} /> Visit Live Platform
                </a>
              )}
              {project.playStore && (
                <a
                  href={project.playStore}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2.5 py-3 px-5 rounded-xl
                    font-semibold text-sm
                    bg-emerald-500/15 border border-emerald-400/30 text-emerald-300
                    hover:bg-emerald-500/25 transition-all"
                >
                  <Smartphone size={15} /> Download on Google Play
                </a>
              )}
              <Link
                to="/"
                className="flex items-center justify-center gap-2.5 py-3 px-5 rounded-xl
                  font-medium text-sm border border-white/10 text-slate-400
                  hover:text-white hover:border-white/20 hover:bg-white/5 transition-all"
              >
                <Github size={15} /> View Portfolio
              </Link>
            </div>
          </div>
        </div>

        {/* ── other projects ── */}
        <OtherProjects currentId={id} />
      </div>

      {/* ── lightbox ── */}
      <AnimatePresence>
        {lightbox && (
          <Lightbox
            src={imgs[current]}
            current={current}
            total={project.count}
            onClose={() => setLightbox(false)}
            onNext={next}
            onPrev={prev}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Other Projects strip ─────────────────────────────── */
function OtherProjects({ currentId }) {
  const others = Object.entries(PROJECTS)
    .filter(([key]) => key !== currentId)
    .slice(0, 4);

  if (!others.length) return null;

  return (
    <div className="mt-16 pt-10 border-t border-white/8">
      <h3 className="text-sm font-mono font-semibold uppercase tracking-widest
        text-slate-500 mb-6">More Projects</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {others.map(([key, p]) => (
          <Link
            key={key}
            to={`/project/${key}`}
            className="group rounded-xl overflow-hidden border border-white/8
              hover:border-cyan-400/35 transition-all bg-white/[0.03]"
          >
            <div className="overflow-hidden h-28 bg-[#0a0b18]">
              <img
                src={require(`./assets/${key}/1.png`)}
                alt={p.name}
                className="w-full h-full object-cover object-top
                  transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="p-3">
              <p className="text-xs font-semibold text-white/80 truncate
                group-hover:text-cyan-300 transition-colors">
                {p.name}
              </p>
              <p className="text-[11px] text-slate-600 mt-0.5">{p.category}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
